import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* A refusal is a real HTTP status carrying { code, message } — there is no
   status field in the body to test. The text is what the screens render, so
   the code is only the fallback when a route answers without a sentence. */
const apiError = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.code || fallback;

/* ================= CREATE PRODUCT =================
   POST /admin/catalog/products binds a JSON body — multipart is a 415, and
   there is no image upload endpoint, so `images` travels as the URL strings
   the form already holds. The answer is { message, data: { id, packs,
   bulkItem } }: a write summary, NOT the product row. */
export const createProduct = createAsyncThunk(
  "product/create",
  async (body, thunkAPI) => {
    try {
      const res = await api.post("/admin/catalog/products", body);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to create product"));
    }
  }
);

/* ================= FETCH ALL PRODUCTS =================
   Server-side list: GET /admin/catalog/products supports page, limit,
   category, subcategory, search (name + productCode, every word in any
   order), status and highlight=wow|gi|popular|bestSeller|millable — the
   service lower-cases the highlight, so the old spelling still matches.
   Every value is URL encoded — an unescaped search like "13 grain" or
   "A&B" used to produce a broken query string.
   The answer is { data, pagination }: that inner `data` is the payload's
   own key, not a leftover envelope, so it is read as before. */
export const fetchProducts = createAsyncThunk(
  "product/fetchAll",
  async (
    { page = 1, limit = 10, category = "", search = "", status = "", highlight = "" } = {},
    thunkAPI
  ) => {
    try {
      const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (category)  qs.set("category",  category);
      if (search)    qs.set("search",    search);
      if (status !== "" && status !== null && status !== undefined) qs.set("status", String(status));
      if (highlight) qs.set("highlight", highlight);
      const res = await api.get(`/admin/catalog/products?${qs.toString()}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to fetch products"));
    }
  }
);

/* ================= FETCH USER PRODUCTS =================
   The customer shelf is GET /shop/products ([no auth]): a cursor, not a
   page — limit/offset — answering { products, limit, offset } with no
   total. Prices ride along as `fromPaise`, in PAISE. */
export const fetchUserProducts = createAsyncThunk(
  "product/fetchUserProducts",
  async ({ page = 1, limit = 9, category = "", bestSelling = false } = {}, thunkAPI) => {
    try {
      const qs = new URLSearchParams({
        limit:  String(limit),
        offset: String((Math.max(page, 1) - 1) * limit),
      });
      if (category)    qs.set("category", category);
      if (bestSelling) qs.set("bestsellers", "true");
      const res = await api.get(`/shop/products?${qs.toString()}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to fetch user products"));
    }
  }
);

/* ================= FETCH PRODUCT BY ID =================
   GET /admin/catalog/products/{id} answers { data: <product>, packs: [] }.
   `data` is the payload's own key here — the product — and the packs are a
   sibling array this store has never carried; a screen that needs them
   reads the endpoint itself. */
export const fetchProductById = createAsyncThunk(
  "product/fetchById",
  async (id, thunkAPI) => {
    try {
      const res = await api.get(`/admin/catalog/products/${id}`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to fetch product"));
    }
  }
);

/* ================= UPDATE PRODUCT =================
   PUT /admin/catalog/products/{id} binds JSON, like the create. It answers
   { message, data: { id, packs, deactivated } } — a write summary, not the
   updated product, so the caller re-reads the detail to show it. */
export const updateProduct = createAsyncThunk(
  "product/update",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await api.put(`/admin/catalog/products/${id}`, data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to update product"));
    }
  }
);

/* ================= TOGGLE PRODUCT STATUS =================
   The three toggles take NO body — each flips the stored flag and answers
   { message, data: { id, name, isActive, isBestseller, isPopular } }. */
export const toggleProductStatus = createAsyncThunk(
  "product/toggleStatus",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/admin/catalog/products/${id}/status`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to toggle status"));
    }
  }
);

/* ================= RETIRE PRODUCT =================
   DELETE no longer deletes: orders, carts, stock and price rows address a
   product by id as TEXT, so the service deactivates it and its packs and
   answers { ok, deactivated, packs }. The row stays, switched off.
   Superadmin only — a branch account gets a 403. */
export const deleteProduct = createAsyncThunk(
  "product/delete",
  async (id, thunkAPI) => {
    try {
      await api.delete(`/admin/catalog/products/${id}`);
      return id;
    } catch (err) {
      return thunkAPI.rejectWithValue(apiError(err, "Failed to retire product"));
    }
  }
);

/* ================= TOGGLE BEST SELLING ================= */
export const toggleProductBestSelling = createAsyncThunk(
  "product/toggleBestSelling",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/admin/catalog/products/${id}/toggle-best-selling`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(apiError(err, "Failed to toggle best selling"));
    }
  }
);

/* ================= TOGGLE POPULAR ================= */
export const toggleProductPopular = createAsyncThunk(
  "product/togglePopular",
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/admin/catalog/products/${id}/toggle-popular`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(apiError(err, "Failed to toggle popular"));
    }
  }
);

/* ================= BULK IMPORT PRODUCTS FROM EXCEL ================= */
export const importProductsFromExcel = createAsyncThunk(
  "product/bulkImport",
  async ({ products, dryRun = false }, { rejectWithValue }) => {
    try {
      // Rows are sent as the sheet had them. The server groups rows sharing a
      // productCode into one product with several packs, so a batch must never
      // split a product's rows across two calls -- the caller batches by whole
      // product for that reason.
      //
      // dryRun makes every write and rolls it back, answering with what WOULD
      // change: the preview the screen shows before anything is applied.
      const response = await api.post(
        `/admin/catalog/products/bulk-import${dryRun ? "?dryRun=true" : ""}`,
        { products });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to import products"
      );
    }
  }
);

/* ================= EXPORT PRODUCTS TO EXCEL ================= */
export const exportProductsToExcel = createAsyncThunk(
  "product/exportToExcel",
  async (_, { rejectWithValue }) => {
    try {
      // TODO(cutover): no server endpoint for this — nothing in the service
      // renders a products workbook.
      const response = await api.get("/api/products/export", {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to export products"
      );
    }
  }
);

/* ================= SLICE ================= */
const productSlice = createSlice({
  name: "product",
  initialState: {
    loading:        false,
    error:          null,
    success:        false,
    products:       [],
    userProducts:   [],
    pagination:     {},
    userPagination: {},
    currentProduct: null,
    importing:      false,
    importResults:  null,
    exportLoading:  false,
  },
  reducers: {
    resetProductState: (state) => {
      state.loading = false;
      state.error   = null;
      state.success = false;
    },
    clearImportResults: (state) => {
      state.importResults = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ── create ── */
      .addCase(createProduct.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(createProduct.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
        /* The answer is { id, packs, bulkItem }, not a table row — unshifting
           it would put a blank product at the top of the list. The caller
           re-reads the page instead. */
      })
      .addCase(createProduct.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── fetchAll ── */
      .addCase(fetchProducts.pending,   (state)         => { state.loading = true; })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading    = false;
        state.products   = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchProducts.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── fetchUserProducts ── */
      .addCase(fetchUserProducts.pending,   (state)         => { state.loading = true; })
      .addCase(fetchUserProducts.fulfilled, (state, action) => {
        state.loading        = false;
        /* { products, limit, offset } — the shelf is a cursor and reports no
           total, so the cursor is all the pagination there is. */
        state.userProducts   = action.payload.products || [];
        state.userPagination = { limit: action.payload.limit, offset: action.payload.offset };
      })
      .addCase(fetchUserProducts.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── fetchById ── */
      .addCase(fetchProductById.pending,   (state)         => { state.loading = true; state.currentProduct = null; })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading        = false;
        state.currentProduct = action.payload;
      })
      .addCase(fetchProductById.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── update ──
         The service answers a write summary, so there is no updated row to
         put back: the screen re-reads the detail it edited. */
      .addCase(updateProduct.pending,   (state)         => { state.loading = true; state.error = null; })
      .addCase(updateProduct.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateProduct.rejected,  (state, action) => { state.loading = false; state.error = action.payload; })

      /* ── toggleStatus ──
         A product is addressed by `id` (its mongo id as TEXT); the service
         returns no `_id` at all, and the flag is `isActive`. */
      .addCase(toggleProductStatus.fulfilled, (state, action) => {
        const idx = state.products.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.products[idx].isActive = action.payload.isActive;
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct.isActive = action.payload.isActive;
        }
      })

      /* ── retire ──
         The product is deactivated, not removed: it stays in the list with
         its status switched off. */
      .addCase(deleteProduct.fulfilled, (state, action) => {
        const idx = state.products.findIndex((p) => p.id === action.payload);
        if (idx !== -1) state.products[idx].isActive = false;
        if (state.currentProduct?.id === action.payload) {
          state.currentProduct.isActive = false;
        }
      })

      /* ── toggleBestSelling ── */
      .addCase(toggleProductBestSelling.fulfilled, (state, action) => {
        const idx = state.products.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.products[idx].isBestseller = action.payload.isBestseller;
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct.isBestseller = action.payload.isBestseller;
        }
      })

      /* ── togglePopular ── */
      .addCase(toggleProductPopular.fulfilled, (state, action) => {
        const idx = state.products.findIndex((p) => p.id === action.payload.id);
        if (idx !== -1) state.products[idx].isPopular = action.payload.isPopular;
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct.isPopular = action.payload.isPopular;
        }
      })

      /* ── bulk import ── */
      .addCase(importProductsFromExcel.pending, (state) => {
        state.importing     = true;
        state.error         = null;
        state.importResults = null;
      })
      .addCase(importProductsFromExcel.fulfilled, (state, action) => {
        state.importing     = false;
        state.importResults = action.payload.data;
        state.loading       = true;
      })
      .addCase(importProductsFromExcel.rejected, (state, action) => {
        state.importing = false;
        state.error     = action.payload;
      })

      /* ── export ── */
      .addCase(exportProductsToExcel.pending,   (state)         => { state.exportLoading = true;  state.error = null; })
      .addCase(exportProductsToExcel.fulfilled, (state)         => { state.exportLoading = false; })
      .addCase(exportProductsToExcel.rejected,  (state, action) => { state.exportLoading = false; state.error = action.payload; });
  },
});

export const { resetProductState, clearImportResults, clearError } = productSlice.actions;
export default productSlice.reducer;

/* ================= SELECTORS ================= */
export const selectAllProducts         = (state) => state.product.products       || [];
export const selectUserProducts        = (state) => state.product.userProducts   || [];
export const selectCurrentProduct      = (state) => state.product.currentProduct;
export const selectProductLoading      = (state) => state.product.loading;
export const selectProductError        = (state) => state.product.error;
export const selectProductSuccess      = (state) => state.product.success;
export const selectProductPagination   = (state) => state.product.pagination;
export const selectUserProductPagination = (state) => state.product.userPagination;
export const selectImporting           = (state) => state.product.importing;
export const selectImportResults       = (state) => state.product.importResults;
export const selectExportLoading       = (state) => state.product.exportLoading;